import qc from 'qc'
import _ from 'lodash'
import iframe from '@ps/bridge/iframe/iframe'
import nodePool from '@ps/bridge/qc/node-pool'
import generateId from '@com/utils/generate-id'
import { Node } from '@antv/x6'
import config from './config'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import {
  getSceneIndex,
  isAudio,
  isDragBound,
  isDragTargetArea,
  isVideo,
} from '@ps/bridge/qc/base'
import { getSwitchSceneEventData } from '@ps/bridge/qc/vp'
import { SwitchSceneEventData } from '@ps/bridge/qc/scripts/editor'

const nodeTypeMap = config.nodeTypes

function getDagNodeEventData(eventData: SwitchSceneEventData[]) {
  const data: {
    [key: string]: {
      action: string[]
      event: string[]
    }
  } = {}
  _.each(eventData, (item) => {
    const id = `${item.nodeId}_${item.switchSceneId}`
    if (data[id]) {
      data[id] = {
        // 旧数据（2022年之前的包)的actionId为 undefined
        // 故事板中连线的计数是用的 action 的长度，所以这里不能去重
        action: [...data[id].action, item.actionId],
        event: [...data[id].event, item.eventId],
      }
    } else {
      data[id] = {
        action: [item.actionId],
        event: [item.eventId],
      }
    }
  })
  return data
}

/**
 * 如果 nodeId 与 switchSceneId 都相同，说明同一个节点有多个事件回调行为跳转到同一个场景，需要去重
 */
function uniqEventData(eventData: SwitchSceneEventData[]) {
  return _.uniqWith(eventData, (value, other) => {
    return (
      value.nodeId === other.nodeId &&
      value.switchSceneId === other.switchSceneId
    )
  })
}

export const portAttrs = {
  circle: {
    r: 12,
    magnet: true,
    stroke: config.borderColor,
    strokeWidth: 4,
    fill: '#fff',
    zIndex: 3,
  },
}

export const invisiblePortAttrs = {
  circle: {
    r: 12,
    magnet: true,
    stroke: 'transparent',
    strokeWidth: 4,
    fill: 'transparent',
    zIndex: 3,
  },
}

export function getPorts({
  isSceneNode,
  attrs,
  outPosition,
}: {
  isSceneNode: boolean
  attrs?: any
  outPosition?: any
}) {
  let groups: {
    [key: string]: {
      position: {
        name: string
      }
      // 暂时写 any
      attrs: any
    }
  } = {
    // 输出链接桩群组定义
    out: {
      position: outPosition || {
        name: 'right',
      },
      attrs: attrs || portAttrs,
    },
  }
  if (isSceneNode) {
    groups = {
      ...groups,
      // 输入链接桩群组定义
      in: {
        position: {
          name: 'left',
        },
        attrs: attrs || portAttrs,
      },
    }
  }

  const items = [
    {
      id: 'port2',
      group: 'out',
    },
  ]
  if (isSceneNode) {
    items.push({
      id: 'port1',
      group: 'in',
    })
  }
  return {
    groups,
    items,
  }
}

export function getGraphData(): {
  nodes: Node.Metadata[]
  scenes: Node.Metadata[]
  edges: Node.Metadata[]
} {
  let switchSceneEventData = getSwitchSceneEventData()

  // 过滤掉在节点池中找不到对应节点的数据，因为事件表可能并没有将数据清理干净
  switchSceneEventData = switchSceneEventData.filter((item) => {
    // nodeId 与 sceneId 是必须可以找到真实的节点
    if (nodePool.find(item.nodeId) && nodePool.find(item.sceneId)) {
      // 如果有跳转场景 id，则去检查，如果没有则不需要检查
      if (item.switchSceneId) {
        return nodePool.find(item.switchSceneId)
      }
      return true
    }
    return false
  })

  const isNeedToRender = (node: qc.Node) => {
    // 忽略拖曳区域节点、音频节点
    if (isDragTargetArea(node) || isDragBound(node) || isAudio(node)) {
      return false
    }
    return true
  }

  // 没有跳转事件的节点
  const dagNodes: Node.Metadata[] = []
  const videoDagNodes: Node.Metadata[] = []
  const sceneList = iframe.gamePlay.children
  sceneList.forEach((scene) => {
    const nodes = getDescendants(scene, null, false)
    nodes.forEach((node) => {
      if (!isNeedToRender(node)) return
      const temp = {
        // 如果是场景节点，nodeId 与 sceneId 相同
        id: node.uuid,
        shape: nodeTypeMap.node,
        width: node.width / config.ratio,
        height: node.height / config.ratio,
        x: 0, // 默认是 0，不要随便改，会导致排版出错
        y: 0, // 默认是 0，不要随便改，会导致排版出错
        data: {
          sceneId: scene.uuid,
          eventId: '',
          actionId: '',
          nodeId: node.uuid,
          switchSceneId: '',
          shape: nodeTypeMap.node,
          nodeMovable: false,
          isSceneNode: false,
          sceneIndex: getSceneIndex(scene.uuid), // 场景节点索引
        },
        ports: getPorts({
          isSceneNode: false,
          outPosition: isVideo(node)
            ? {
                name: 'absolute',
                args: {
                  x: node.width / 2,
                  y: node.height / 4 - 60,
                },
              }
            : null,
          attrs: {
            circle: {
              r: 12,
              magnet: true,
              stroke: 'transparent',
              strokeWidth: 4,
              fill: 'transparent',
              zIndex: 3,
            },
          },
        }),
      }
      if (isVideo(nodePool.find(node.uuid as string))) {
        videoDagNodes.push(temp)
      } else {
        dagNodes.push(temp)
      }
    })
  })
  let allDagNodes = [...videoDagNodes, ...dagNodes]

  // 计算同一个节点有多少事件回调行为跳转到同一个场景（必须在去重之前计算）
  const dagNodeEventData = getDagNodeEventData(switchSceneEventData)
  // 去重
  switchSceneEventData = uniqEventData(switchSceneEventData)

  const graphData = _.flatten(
    switchSceneEventData.map((eventDataItem, index) => {
      // 是否是场景节点
      const isSceneNode = eventDataItem.nodeId === eventDataItem.sceneId
      const shape = isSceneNode ? nodeTypeMap.scene : nodeTypeMap.node
      const node = nodePool.find(eventDataItem.nodeId)
      // playable 是两倍大小，所以需要除以 2
      // 如果是场景节点的话，node.width = 375 * 2，node.height = 667 * 2
      const width = node.width / config.ratio
      const height = node.height / config.ratio

      // 场景节点索引
      const sceneIndex = getSceneIndex(eventDataItem.sceneId)

      const getShapeData = (isSceneNode: boolean) => {
        const ports = getPorts({
          isSceneNode,
          outPosition: isVideo(node)
            ? {
                name: 'absolute',
                args: {
                  x: node.width / 2,
                  y: node.height / 4 - 60,
                },
              }
            : null,
        })
        const shapeData: Node.Metadata[] = [
          {
            // 如果是场景节点，nodeId 与 sceneId 相同
            id: eventDataItem.nodeId,
            shape,
            width,
            height,
            x: 0, // 默认是 0，不要随便改，会导致排版出错
            y: 0, // 默认是 0，不要随便改，会导致排版出错
            data: {
              ...eventDataItem,
              shape,
              nodeMovable: isSceneNode,
              isSceneNode,
              sceneIndex, // 场景节点索引
            },
            ports,
          },
        ]
        return shapeData
      }
      const shapeData = getShapeData(isSceneNode)

      // 如果有目标场景节点，则新增边节点
      if (eventDataItem.switchSceneId) {
        const currentDagNodeEventData =
          dagNodeEventData[
            `${eventDataItem.nodeId}_${eventDataItem.switchSceneId}`
          ]
        shapeData.push({
          id: generateId(),
          shape: nodeTypeMap.edge,
          source: {
            cell: eventDataItem.nodeId,
            port: `port2`,
          },
          target: {
            cell: eventDataItem.switchSceneId,
            port: `port1`,
          },
          zIndex: config.edgeZIndex,
          data: {
            ...eventDataItem,
            actionId: '',
            eventId: '',
            // 边 actionId 与 eventId 可能存在多个
            actionIds: currentDagNodeEventData.action,
            eventIds: currentDagNodeEventData.event,
            shape: nodeTypeMap.edge,
            nodeMovable: true,
            isSceneNode,
            sceneIndex,
          },
        })
      }

      return shapeData
    }),
  )

  const dagNodeWhichHadSwitchScene = graphData.filter(
    (item) => item.shape === nodeTypeMap.node,
  )
  // 保证排序正确，将有事件的节点数据放到合适的位置
  allDagNodes = allDagNodes.map((node) => {
    if (dagNodeWhichHadSwitchScene.find((item) => item.id === node.id)) {
      return dagNodeWhichHadSwitchScene.find(
        (item) => item.id === node.id,
      ) as Node.Metadata
    }
    return node
  })

  return {
    scenes: graphData.filter((item) => item.shape === nodeTypeMap.scene),
    edges: graphData.filter((item) => item.shape === nodeTypeMap.edge),
    nodes: allDagNodes,
  }
}
